import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../supabase';
import SchoolSidebar from '../SchoolSidebar';
import { SchoolMobileHeader } from '../SchoolMobileHeader';
import { useBranchFilter } from '../../../hooks/useBranchFilter';
import '../SchoolDashboard.css';

function ReportView() {
  const navigate = useNavigate();
  const { reportType } = useParams();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchReportData();
    }
  }, [schoolData?.id, selectedBranchId, reportType]);

  const fetchReportData = async () => {
    try {
      let query;
      let dataKey = '';

      switch (reportType) {
        case 'student-report':
        case 'student-list':
          // Fetch students with class information
          let studentsQuery = supabase
            .from('students')
            .select(`
              *,
              classes:class_id (
                id,
                class_name
              )
            `)
            .eq('school_id', schoolData.id)
            .eq('is_active', true)
            .order('first_name', { ascending: true });
          
          if (selectedBranchId) {
            studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
          }
          
          const { data: studentsData, error: studentsError } = await studentsQuery;
          
          if (studentsError) {
            console.error('Error fetching students:', studentsError);
            setData([]);
            return;
          }
          
          // Format student data for display
          const formattedStudents = (studentsData || []).map(student => {
            const className = student.classes?.class_name || 'No Class';
            const getClassDisplayName = (name) => {
              if (!name) return 'No Class';
              if (name.includes(' - ')) {
                return name.split(' - ')[0].trim();
              }
              return name.trim();
            };
            
            return {
              student_id: student.student_id_no || student.student_id || student.id || 'N/A',
              full_name: `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim() || 'N/A',
              first_name: student.first_name || 'N/A',
              last_name: student.last_name || 'N/A',
              email: student.email || 'N/A',
              phone: student.phone || 'N/A',
              class_name: getClassDisplayName(className),
              section: student.section_id || student.section || 'N/A',
              date_of_birth: student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString() : 'N/A',
              gender: student.gender || 'N/A',
              nationality: student.nationality || 'N/A',
              admission_date: student.admission_date ? new Date(student.admission_date).toLocaleDateString() : 'N/A',
              parent_name: student.parent_name || 'N/A',
              address: student.address || 'N/A',
              is_active: student.is_active ? 'Active' : 'Inactive'
            };
          });
          
          setData(formattedStudents);
          prepareChartData(formattedStudents, reportType);
          return;

        case 'staff-report':
          // Combine teachers, receptionists, drivers, cooks
          let teachersQuery = supabase
            .from('teachers')
            .select('*')
            .eq('school_id', schoolData.id)
            .eq('is_active', true);
          
          let receptionistsQuery = supabase
            .from('receptionists')
            .select('*')
            .eq('school_id', schoolData.id)
            .eq('is_active', true);
          
          let driversQuery = supabase
            .from('transport_persons')
            .select('*')
            .eq('school_id', schoolData.id)
            .eq('is_active', true);
          
          let cooksQuery = supabase
            .from('cooks')
            .select('*')
            .eq('school_id', schoolData.id)
            .eq('is_active', true);
          
          // Apply branch filtering if selected
          if (selectedBranchId) {
            teachersQuery = teachersQuery.eq('branch_id', selectedBranchId);
            receptionistsQuery = receptionistsQuery.eq('branch_id', selectedBranchId);
            driversQuery = driversQuery.eq('branch_id', selectedBranchId);
            cooksQuery = cooksQuery.eq('branch_id', selectedBranchId);
          }
          
          const [teachers, receptionists, drivers, cooks] = await Promise.all([
            teachersQuery,
            receptionistsQuery,
            driversQuery,
            cooksQuery
          ]);
          
          // Format staff data with consistent structure
          const allStaff = [
            ...(teachers.data || []).map(t => ({ 
              ...t, 
              user_type: 'Teacher',
              full_name: `${t.first_name || ''} ${t.middle_name || ''} ${t.last_name || ''}`.trim(),
              role: 'Teacher',
              employee_id: t.employee_id || 'N/A',
              email: t.email || 'N/A',
              phone: t.phone || 'N/A',
              qualification: t.qualification || 'N/A',
              joining_date: t.joining_date || 'N/A',
              salary: t.salary || 'N/A'
            })),
            ...(receptionists.data || []).map(r => ({ 
              ...r, 
              user_type: 'Receptionist',
              full_name: `${r.first_name || ''} ${r.middle_name || ''} ${r.last_name || ''}`.trim(),
              role: 'Receptionist',
              employee_id: r.employee_id || 'N/A',
              email: r.email || 'N/A',
              phone: r.phone || 'N/A',
              qualification: r.qualification || 'N/A',
              joining_date: r.joining_date || 'N/A',
              salary: r.salary || 'N/A'
            })),
            ...(drivers.data || []).map(d => ({ 
              ...d, 
              user_type: 'Driver',
              full_name: `${d.first_name || ''} ${d.middle_name || ''} ${d.last_name || ''}`.trim(),
              role: 'Driver',
              employee_id: d.employee_id || 'N/A',
              email: d.email || 'N/A',
              phone: d.phone || 'N/A',
              license_number: d.license_number || 'N/A',
              vehicle_assigned: d.vehicle_id || 'N/A',
              joining_date: d.joining_date || 'N/A',
              salary: d.salary || 'N/A'
            })),
            ...(cooks.data || []).map(c => ({ 
              ...c, 
              user_type: 'Cook',
              full_name: `${c.first_name || ''} ${c.middle_name || ''} ${c.last_name || ''}`.trim(),
              role: 'Cook',
              employee_id: c.employee_id || 'N/A',
              email: c.email || 'N/A',
              phone: c.phone || 'N/A',
              joining_date: c.joining_date || 'N/A',
              salary: c.salary || 'N/A'
            }))
          ];
          
          setData(allStaff);
          prepareChartData(allStaff, 'staff');
          return;

        case 'fee-report':
          let feeQuery = supabase
            .from('student_fees')
            .select(`
              *,
              students:student_id (
                id,
                first_name,
                last_name,
                student_id_no,
                class_id,
                section_id
              ),
              classes:class_id (
                id,
                class_name
              ),
              fee_schedules:fee_schedule_id (
                id,
                fee_name,
                amount
              )
            `)
            .eq('school_id', schoolData.id)
            .order('created_at', { ascending: false });
          
          if (selectedBranchId) {
            feeQuery = feeQuery.eq('branch_id', selectedBranchId);
          }
          
          const { data: feesData, error: feesError } = await feeQuery;
          
          if (feesError) {
            console.error('Error fetching fees:', feesError);
            setData([]);
            return;
          }
          
          // Format fee data for display
          const formattedFees = (feesData || []).map(fee => ({
            ...fee,
            student_name: fee.students ? `${fee.students.first_name || ''} ${fee.students.last_name || ''}`.trim() : 'N/A',
            student_id_no: fee.students?.student_id_no || 'N/A',
            class_name: fee.classes?.class_name || 'N/A',
            fee_name: fee.fee_schedules?.fee_name || fee.fee_type || 'N/A',
            total_amount: parseFloat(fee.amount || 0),
            paid_amount: parseFloat(fee.paid_amount || 0),
            discount_amount: parseFloat(fee.discount_amount || 0),
            balance: parseFloat(fee.amount || 0) - parseFloat(fee.paid_amount || 0) - parseFloat(fee.discount_amount || 0),
            status: fee.status || fee.payment_status || 'pending',
            due_date: fee.due_date || 'N/A',
            payment_date: fee.payment_date || fee.updated_at || 'N/A'
          }));
          
          setData(formattedFees);
          prepareChartData(formattedFees, 'fee-report');
          return;

        case 'transaction-report':
          query = supabase
            .from('student_fees')
            .select('*')
            .eq('school_id', schoolData.id)
            .eq('status', 'paid');
          if (selectedBranchId) query = query.eq('branch_id', selectedBranchId);
          dataKey = 'transactions';
          break;

        case 'class-list':
          query = supabase
            .from('classes')
            .select('*')
            .eq('school_id', schoolData.id);
          if (selectedBranchId) query = query.eq('branch_id', selectedBranchId);
          dataKey = 'classes';
          break;

        case 'transport-list':
          query = supabase
            .from('routes')
            .select('*')
            .eq('school_id', schoolData.id);
          if (selectedBranchId) query = query.eq('branch_id', selectedBranchId);
          dataKey = 'transport';
          break;

        default:
          return;
      }

      const { data: result, error } = await query;
      if (error) throw error;

      setData(result || []);
      prepareChartData(result || [], reportType);
    } catch (error) {
      console.error('Error fetching report data:', error);
    }
  };

  const prepareChartData = (data, type) => {
    let chart = [];

    switch (type) {
      case 'student-report':
      case 'student-list':
        // Group by class_name
        const classGroups = {};
        data.forEach(student => {
          const className = student.class_name || 'Unassigned';
          classGroups[className] = (classGroups[className] || 0) + 1;
        });
        chart = Object.entries(classGroups).map(([name, value]) => ({ name: name, value }));
        break;

      case 'staff':
        // Group by staff type
        const staffGroups = {};
        data.forEach(staff => {
          const type = staff.user_type || 'other';
          staffGroups[type] = (staffGroups[type] || 0) + 1;
        });
        chart = Object.entries(staffGroups).map(([name, value]) => ({ name, value }));
        break;

      case 'fee-report':
        // Group by status
        const statusGroups = {};
        data.forEach(fee => {
          const status = fee.status || 'unknown';
          statusGroups[status] = (statusGroups[status] || 0) + 1;
        });
        chart = Object.entries(statusGroups).map(([name, value]) => ({ name, value }));
        break;

      case 'transaction-report':
        // Group by month
        const monthGroups = {};
        data.forEach(transaction => {
          const date = new Date(transaction.created_at || transaction.payment_date);
          const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });
          monthGroups[month] = (monthGroups[month] || 0) + 1;
        });
        chart = Object.entries(monthGroups).map(([name, value]) => ({ name, value }));
        break;

      case 'class-list':
        // Group by sections
        const sectionGroups = {};
        data.forEach(cls => {
          const sections = cls.sections || [];
          sections.forEach(section => {
            sectionGroups[section] = (sectionGroups[section] || 0) + 1;
          });
        });
        chart = Object.entries(sectionGroups).map(([name, value]) => ({ name, value }));
        break;

      default:
        chart = [];
    }

    setChartData(chart);
  };

  const getReportTitle = () => {
    const titles = {
      'student-report': 'Student Report',
      'staff-report': 'Staff Report',
      'fee-report': 'Fee Report',
      'transaction-report': 'Transaction Report',
      'class-list': 'Class List',
      'student-list': 'Student List',
      'transport-list': 'Transport List'
    };
    return titles[reportType] || 'Report';
  };

  const handleDownload = () => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(header => {
      const value = row[header];
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      return value || '';
    }));
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  };

  const getMaxValue = () => {
    if (chartData.length === 0) return 1;
    return Math.max(...chartData.map(d => d.value));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader schoolData={schoolData} />
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content">
        <div className="content-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>{getReportTitle()}</h1>
            <div>
              <button onClick={handleDownload} className="btn-secondary" style={{ marginRight: '0.5rem' }}>
                Download CSV
              </button>
              <button onClick={() => window.print()} className="btn-secondary">
                Print
              </button>
            </div>
          </div>
        </div>

        <div className="bi-reports-container">
          {/* Chart Section */}
          {chartData.length > 0 && (
            <div className="dashboard-section">
              <h2>Visualization</h2>
              <div className="charts-container">
                {/* Bar Chart */}
                <div className="chart-wrapper">
                  <h3>Bar Chart</h3>
                  <div className="bar-chart">
                    {chartData.map((item, idx) => {
                      const percentage = (item.value / getMaxValue()) * 100;
                      return (
                        <div key={idx} className="bar-item">
                          <div className="bar-label">{item.name}</div>
                          <div className="bar-container">
                            <div 
                              className="bar-fill" 
                              style={{ 
                                width: `${percentage}%`,
                                backgroundColor: `hsl(${idx * 360 / chartData.length}, 70%, 50%)`
                              }}
                            >
                              <span className="bar-value">{item.value}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Pie Chart */}
                <div className="chart-wrapper">
                  <h3>Pie Chart</h3>
                  <div className="pie-chart">
                    <svg viewBox="0 0 200 200" className="pie-svg">
                      {(() => {
                        const total = chartData.reduce((sum, d) => sum + d.value, 0);
                        let currentAngle = -90;
                        return chartData.map((item, idx) => {
                          const percentage = (item.value / total) * 100;
                          const angle = (percentage / 100) * 360;
                          const startAngle = currentAngle;
                          currentAngle += angle;
                          
                          const x1 = 100 + 80 * Math.cos((startAngle * Math.PI) / 180);
                          const y1 = 100 + 80 * Math.sin((startAngle * Math.PI) / 180);
                          const x2 = 100 + 80 * Math.cos((currentAngle * Math.PI) / 180);
                          const y2 = 100 + 80 * Math.sin((currentAngle * Math.PI) / 180);
                          const largeArc = angle > 180 ? 1 : 0;
                          
                          return (
                            <path
                              key={idx}
                              d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                              fill={`hsl(${idx * 360 / chartData.length}, 70%, 50%)`}
                              stroke="white"
                              strokeWidth="2"
                            />
                          );
                        });
                      })()}
                    </svg>
                    <div className="pie-legend">
                      {chartData.map((item, idx) => (
                        <div key={idx} className="legend-item">
                          <span 
                            className="legend-color" 
                            style={{ backgroundColor: `hsl(${idx * 360 / chartData.length}, 70%, 50%)` }}
                          ></span>
                          <span>{item.name}: {item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="dashboard-section">
            <h2>Data Table</h2>
            {data.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                No data available for this report.
              </p>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {reportType === 'staff-report' ? (
                          <>
                            <th style={{ textAlign: 'center' }}>Staff Type</th>
                            <th style={{ textAlign: 'center' }}>Full Name</th>
                            <th style={{ textAlign: 'center' }}>Employee ID</th>
                            <th style={{ textAlign: 'center' }}>Email</th>
                            <th style={{ textAlign: 'center' }}>Phone</th>
                            <th style={{ textAlign: 'center' }}>Qualification</th>
                            <th style={{ textAlign: 'center' }}>Joining Date</th>
                            <th style={{ textAlign: 'center' }}>Salary</th>
                          </>
                        ) : reportType === 'fee-report' ? (
                          <>
                            <th style={{ textAlign: 'center' }}>Student Name</th>
                            <th style={{ textAlign: 'center' }}>Student ID</th>
                            <th style={{ textAlign: 'center' }}>Class</th>
                            <th style={{ textAlign: 'center' }}>Fee Name</th>
                            <th style={{ textAlign: 'center' }}>Total Amount</th>
                            <th style={{ textAlign: 'center' }}>Paid Amount</th>
                            <th style={{ textAlign: 'center' }}>Discount</th>
                            <th style={{ textAlign: 'center' }}>Balance</th>
                            <th style={{ textAlign: 'center' }}>Status</th>
                            <th style={{ textAlign: 'center' }}>Due Date</th>
                            <th style={{ textAlign: 'center' }}>Payment Date</th>
                          </>
                        ) : reportType === 'student-report' || reportType === 'student-list' ? (
                          <>
                            <th style={{ textAlign: 'center' }}>Student ID</th>
                            <th style={{ textAlign: 'center' }}>Full Name</th>
                            <th style={{ textAlign: 'center' }}>Email</th>
                            <th style={{ textAlign: 'center' }}>Phone</th>
                            <th style={{ textAlign: 'center' }}>Class</th>
                            <th style={{ textAlign: 'center' }}>Section</th>
                            <th style={{ textAlign: 'center' }}>Date of Birth</th>
                            <th style={{ textAlign: 'center' }}>Gender</th>
                            <th style={{ textAlign: 'center' }}>Nationality</th>
                            <th style={{ textAlign: 'center' }}>Admission Date</th>
                            <th style={{ textAlign: 'center' }}>Parent Name</th>
                            <th style={{ textAlign: 'center' }}>Status</th>
                          </>
                        ) : (
                          data.length > 0 && Object.keys(data[0]).slice(0, 10).map((key, idx) => (
                            <th key={idx} style={{ textAlign: 'center' }}>
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </th>
                          ))
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data.slice(0, 50).map((row, idx) => (
                        <tr key={idx}>
                          {reportType === 'staff-report' ? (
                            <>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: row.user_type === 'Teacher' ? '#8B5CF6' : 
                                                  row.user_type === 'Receptionist' ? '#10B981' :
                                                  row.user_type === 'Driver' ? '#3B82F6' : '#F59E0B',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  {row.user_type || 'N/A'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>{row.full_name || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.employee_id || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.email || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.phone || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.qualification || row.license_number || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.joining_date ? new Date(row.joining_date).toLocaleDateString() : 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.salary ? `$${parseFloat(row.salary).toFixed(2)}` : 'N/A'}</td>
                            </>
                          ) : reportType === 'fee-report' ? (
                            <>
                              <td style={{ textAlign: 'center' }}>{row.student_name || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.student_id_no || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.class_name || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.fee_name || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>${row.total_amount.toFixed(2)}</td>
                              <td style={{ textAlign: 'center', color: '#10B981' }}>${row.paid_amount.toFixed(2)}</td>
                              <td style={{ textAlign: 'center', color: '#F59E0B' }}>${row.discount_amount.toFixed(2)}</td>
                              <td style={{ textAlign: 'center', color: row.balance > 0 ? '#EF4444' : '#10B981', fontWeight: '600' }}>
                                ${row.balance.toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: row.status === 'paid' ? '#10B981' : 
                                                  row.status === 'unpaid' ? '#EF4444' :
                                                  row.status === 'partial' ? '#F59E0B' : '#6B7280',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  textTransform: 'capitalize'
                                }}>
                                  {row.status || 'pending'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {row.due_date && row.due_date !== 'N/A' ? new Date(row.due_date).toLocaleDateString() : 'N/A'}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {row.payment_date && row.payment_date !== 'N/A' ? new Date(row.payment_date).toLocaleDateString() : 'N/A'}
                              </td>
                            </>
                          ) : reportType === 'student-report' || reportType === 'student-list' ? (
                            <>
                              <td style={{ textAlign: 'center' }}>{row.student_id || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.full_name || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.email || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.phone || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.class_name || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.section || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.date_of_birth || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.gender || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.nationality || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.admission_date || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>{row.parent_name || 'N/A'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: row.is_active === 'Active' ? '#10B981' : '#EF4444',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  {row.is_active || 'N/A'}
                                </span>
                              </td>
                            </>
                          ) : (
                            Object.keys(row).slice(0, 10).map((key, keyIdx) => (
                              <td key={keyIdx} style={{ textAlign: 'center' }}>
                                {typeof row[key] === 'object' ? JSON.stringify(row[key]) : String(row[key] || '')}
                              </td>
                            ))
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.length > 50 && (
                  <p style={{ textAlign: 'center', marginTop: '1rem', color: '#6c757d' }}>
                    Showing first 50 of {data.length} records
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportView;
